<script setup lang="ts">
import {
  IonLoading,
  IonGrid,
  IonRow,
  IonCol,
  IonIcon,
  IonPage,
  IonFooter,
  IonNote,
  IonSelect,
  IonSelectOption,
  IonContent,
  IonList,
  IonLabel,
  IonItem,
  IonInput,
  IonButton,
  onIonViewWillEnter,
  alertController,
} from "@ionic/vue";
import { closeCircle, save } from "ionicons/icons";
import countries from "countries-list";
import { useVisitors } from ".";
import { useRoute } from "vue-router";
import { deleteVisitor } from "@/api/visitors";
import { useToasters } from "@/composition/toasters";
import router from "@/router";
import { handleError } from "@/views";

const countriesOptions = Object.keys(countries.countries).map((key) => ({
  label: countries.countries[key].name,
  value: key,
}));

const route = useRoute();
const toasters = useToasters();
const { state, form, data, loadVisitorInfo, update } = useVisitors();

const visitor_id = parseInt(route.params.visitor_id as string, 10);
const event_id = parseInt(route.params.event_id as string, 10);

const updateVisitor = () => {
  update().then(() => {
    router.push(`/event/${event_id}/visitors`);
  })
}
const deleteVisitorPrompt = async () => {
  const alert = await alertController.create({
    header: "Are you sure?",
    buttons: [
      {
        text: "Cancel",
        role: "cancel",
      },
      {
        text: "Delete",
        role: "confirm",
        handler: () => {
          deleteVisitor(data.value)
            .then(() => {
              toasters.addSuccess(
                `Visitor ${data.value.name} deleted successfuly`
              );
              router.push(`/event/${event_id}/visitors`);
              alert.remove();
            })
            .catch(handleError);
        },
      },
    ],
  });

  await alert.present();
};

onIonViewWillEnter(() => {
  loadVisitorInfo(visitor_id);
});
</script>
<template>
  <ion-page>
    <ion-content>
      <ion-list>
        <ion-list>
          <ion-item :class="{ 'ion-invalid': state.errors.name.length > 0 }">
            <ion-label position="stacked">Name</ion-label>
            <ion-input placeholder="John Doe" v-model="form.name"></ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.name"
              :key="`name-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
          <ion-item :class="{ 'ion-invalid': state.errors.email.length > 0 }">
            <ion-label position="stacked"> Email</ion-label>
            <ion-input
              type="email"
              placeholder="john.doe@gmail.com"
              v-model="form.email"
              disabled
            >
            </ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.email"
              :key="`email-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
          <ion-item
            :class="{
              'ion-invalid': state.errors.phone_number.length > 0,
            }"
          >
            <ion-label position="stacked">Phone number</ion-label>
            <ion-input
              type="tel"
              placeholder="+1 123 456 7890"
              v-model="form.phone_number"
            >
            </ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.phone_number"
              :key="`phone-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
          <ion-item
            :class="{
              'ion-invalid': state.errors.birthdate.length > 0,
            }"
          >
            <ion-label position="stacked">Birth date</ion-label>
            <ion-input
              type="date"
              placeholder="Microsoft"
              v-model="form.birthdate"
            >
            </ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.birthdate"
              :key="`phone-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
          <ion-item
            :class="{
              'ion-invalid': state.errors.company.length > 0,
            }"
          >
            <ion-label position="stacked">Company</ion-label>
            <ion-input
              type="text"
              placeholder="Company Name"
              v-model="form.company"
            >
            </ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.company"
              :key="`company-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
          <ion-item :class="{ 'ion-invalid': state.errors.country.length > 0 }">
            <ion-label position="stacked">Country</ion-label>
            <ion-select
              placeholder="Select your country"
              v-model="form.country"
            >
              <ion-select-option
                :value="option.value"
                v-for="option in countriesOptions"
                :key="`country-option-${option.value}`"
                >{{ option.label }}</ion-select-option
              >
            </ion-select>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.country"
              :key="`job-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>

          <ion-item
            :class="{
              'ion-invalid': state.errors.job_title.length > 0,
            }"
          >
            <ion-label position="stacked">Job title</ion-label>
            <ion-input
              type="text"
              placeholder="Lead Software Developer"
              v-model="form.job_title"
            >
            </ion-input>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.job_title"
              :key="`job-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>

          <ion-item
            v-if="state.visitor_types.length > 0"
            :class="{
              'ion-invalid': state.errors.visitor_type.length > 0,
            }"
          >
            <ion-label position="stacked">Visitor type</ion-label>
            <ion-select
              placeholder="Select visitor type"
              v-model="form.visitor_type"
            >
              <ion-select-option
                :value="visitor_type.id"
                v-for="visitor_type in state.visitor_types"
                :key="`visitor-type-${visitor_type.id}`"
                >{{ visitor_type.name }}</ion-select-option
              >
            </ion-select>
            <ion-note
              slot="error"
              v-for="(message, i) in state.errors.visitor_type"
              :key="`type-error-${i}`"
            >
              {{ message }}
            </ion-note>
          </ion-item>
        </ion-list>
      </ion-list>
      <ion-button
        fill="clear"
        color="danger"
        expand="block"
        @click="deleteVisitorPrompt()"
      >
        Delete visitor
      </ion-button>
    </ion-content>
    <ion-footer>
      <div class="container">
        <section>
          <ion-grid>
            <ion-row>
              <ion-col>
                <ion-button
                  expand="block"
                  color="light"
                  :router-link="`/event/${event_id}/visitor/${visitor_id}`"
                >
                  <ion-icon slot="start" :icon="closeCircle"></ion-icon>
                  Cancel
                </ion-button>
              </ion-col>
              <ion-col>
                <ion-button expand="block" color="success" @click="updateVisitor()">
                  <ion-icon slot="start" :icon="save"></ion-icon>
                  Save
                </ion-button>
              </ion-col>
            </ion-row>
          </ion-grid>
        </section>
      </div>
    </ion-footer>
    <ion-loading :is-open="state.loading" message="Please wait...">
    </ion-loading>
  </ion-page>
</template>
